// Cozy product page (Poofiz): variant + quantity state, swipe gallery, quantity breaks,
// bundle upgrade, sticky bar, delivery dates and scroll reveals.
// One add-to-cart path: everything posts through CozyProduct.addItems(), which hands the
// response to the cart drawer via the cancelable `cozy:cart-add` event.

// Reduced motion = the OS setting or "stop animations" in the accessibility menu (cozy-a11y.js).
const cozyReduceMotion = () =>
  window.cozyCalm ? window.cozyCalm() : matchMedia('(prefers-reduced-motion: reduce)').matches;

// Shopify money format → string. Trailing zeros are trimmed to match Liquid's
// money_without_trailing_zeros (₪249, not ₪249.00).
function cozyMoney(cents, format) {
  const value = Number(cents) / 100;
  const fmt = (n, decimals, thousands = ',', dec = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac && Number(frac) !== 0 ? grouped + dec + frac : grouped;
  };
  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return fmt(value, 0);
      case 'amount_with_comma_separator':
        return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(value, 2, "'", '.');
      default:
        return fmt(value, 2);
    }
  });
}

// Scroll reveal: elements already on screen are left alone (no flash); the rest are "armed"
// (CSS hides/empties them) and revealed once when they scroll in.
function cozyReveal(root = document) {
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    },
    { threshold: 0.2 }
  );
  root.querySelectorAll('[data-reveal]:not([data-reveal-armed])').forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.setAttribute('data-reveal-armed', '');
    io.observe(el);
  });
}

if (!customElements.get('cozy-product')) {
  customElements.define(
    'cozy-product',
    class CozyProduct extends HTMLElement {
      connectedCallback() {
        this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-cozy-strings]').textContent);
        this.live = this.querySelector('[data-live]');
        this.initGallery();
        this.initNotes();

        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.form) return;
        this.idInput = this.form.querySelector('input[name="id"]');
        this.qtyInput = this.form.querySelector('input[name="quantity"]');
        this.variant = this.product.variants.find((v) => String(v.id) === this.idInput.value) || this.product.variants[0];

        const stockMap = this.querySelector('[data-lowstock-map]');
        this.lowStock = stockMap ? JSON.parse(stockMap.textContent) : {};

        this.addEventListener('change', this.onChange);
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          // Follow-up 2026-10-20: no garment size, nothing is added: go to the size and ask for it.
          if (this.askSize()) return;
          this.addItems([{ id: Number(this.idInput.value), quantity: Number(this.qtyInput.value) || 1 }]);
        });
        this.querySelector('[data-upsell-add]')?.addEventListener('click', (e) => {
          this.addItems([{ id: Number(e.currentTarget.dataset.upsellAdd), quantity: 1 }], e.currentTarget);
        });

        this.updateOptionAvailability();
        this.initSticky();
        // Follow-up 2026-10-20: no size preselected in the sticky bar's select either.
        const stickySel = this.querySelector('[data-sticky-variant]');
        if (stickySel && this.querySelector('[data-size-required]')) {
          const ph = new Option('בחרו מידה', '', true, true);
          ph.disabled = true;
          stickySel.prepend(ph);
          stickySel.value = '';
        }

        // CRO 2026-10-20: colour photos ready before the tap; the folded offer cards open on their row.
        const picker = this.querySelector('[data-variant-picker]');
        if (picker) {
          const warm = () => this.warmColours();
          ['pointerdown', 'touchstart', 'focusin', 'mouseover'].forEach((t) => picker.addEventListener(t, warm, { once: true, passive: true }));
          const idle = () => setTimeout(() => (window.requestIdleCallback || setTimeout)(warm), 3000);
          if (document.readyState === 'complete') idle();
          else window.addEventListener('load', idle, { once: true });
        }
        this.querySelector('[data-breaks-toggle]')?.addEventListener('click', (e) => {
          const wrap = e.currentTarget.closest('[data-breaks]');
          wrap.classList.add('is-open');
          e.currentTarget.setAttribute('aria-expanded', 'true');
          e.currentTarget.hidden = true;
          wrap.querySelector('[data-break-input]:checked')?.focus({ preventScroll: true });
        });
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.openFromHash);
        this.stickyObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.stickyResize?.disconnect();
        this.stickyMedia?.removeEventListener('change', this.onStickyMedia);
      }

      /* ---------- State ---------- */

      onChange = (e) => {
        const t = e.target;
        if (t.matches('[data-option-index]')) {
          const options = this.selectedOptions();
          // Follow-up 2026-10-20: an unchosen garment size matches any size (the photo/price follow the colour).
          const fits = (v) => v.options.every((o, i) => options[i] === undefined || o === options[i]);
          const full = options.every((o) => o !== undefined);
          const v = full ? this.product.variants.find(fits) : this.product.variants.find((x) => fits(x) && x.available) || this.product.variants.find(fits);
          if (full) this.hideSizeNeed();
          this.setVariant(v);
        } else if (t.matches('[data-break-input]')) {
          this.qtyInput.value = t.value;
          this.updateTotal();
        } else if (t.matches('[data-sticky-variant]')) {
          const variant = this.product.variants.find((v) => String(v.id) === t.value);
          this.syncRadios(variant);
          this.hideSizeNeed();
          this.setVariant(variant);
        }
      };

      selectedOptions() {
        return [...this.querySelectorAll('[data-option-fieldset]')].map((fs) => fs.querySelector('input:checked')?.value);
      }

      syncRadios(variant) {
        if (!variant) return;
        this.querySelectorAll('[data-option-fieldset]').forEach((fs, i) => {
          fs.querySelectorAll('input').forEach((input) => (input.checked = input.value === variant.options[i]));
        });
      }

      setVariant(variant) {
        const atcs = this.querySelectorAll('[data-atc]');
        if (!variant) {
          atcs.forEach((b) => {
            b.disabled = true;
            b.querySelector('[data-atc-label]').textContent = this.strings.unavailable;
          });
          this.querySelectorAll('[data-sticky-variant-title]').forEach((el) => (el.textContent = this.selectedOptions().filter(Boolean).join(' / ')));
          this.updateOptionAvailability();
          return;
        }

        this.variant = variant;
        this.idInput.value = variant.id;
        const money = (c) => cozyMoney(c, this.strings.money_format);

        this.querySelectorAll('[data-option-fieldset]').forEach((fs, i) => {
          const label = fs.querySelector('[data-option-selected]');
          if (!label) return;
          const open = fs.hasAttribute('data-size-required') && !fs.querySelector('input:checked');
          label.textContent = open ? `בחרו ${fs.dataset.sizeName || 'מידה'}` : variant.options[i];
          label.classList.toggle('is-empty', open);
        });

        // The kit (.cozy-product--kit) never shows its compare-at as a "regular price" or a % badge (legal, 2026-10-09):
        // its reference is the separate-purchase total, kept by the kit builder ([data-kb-save-for]).
        const onSale = variant.compare_at_price > variant.price && !this.classList.contains('cozy-product--kit');
        this.querySelectorAll('[data-price]').forEach((el) => (el.textContent = money(variant.price)));
        this.querySelectorAll('[data-compare-price]').forEach((el) => {
          el.hidden = !onSale;
          if (onSale) (el.querySelector('[data-compare-amount]') || el).textContent = money(variant.compare_at_price);
        });
        // The launch price badge never sits beside a struck compare-at price.
        this.querySelectorAll('[data-launch-badge]').forEach((el) => (el.hidden = onSale));
        this.querySelectorAll('[data-sale-badge]').forEach((el) => {
          el.hidden = !onSale;
          if (onSale) {
            const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
            el.textContent = this.strings.sale_badge.replace('[percent]', pct);
          }
        });

        atcs.forEach((b) => {
          b.disabled = !variant.available;
          b.querySelector('[data-atc-label]').textContent = variant.available ? this.strings.add : this.strings.sold_out;
        });

        const low = this.querySelector('[data-lowstock]');
        if (low) {
          const count = this.lowStock[variant.id] || 0;
          low.hidden = !count;
          if (count) low.querySelector('[data-lowstock-text]').textContent = low.dataset.template.replace('[count]', count);
        }

        const stickySelect = this.querySelector('[data-sticky-variant]');
        const sizeOpen = [...this.querySelectorAll('[data-size-required]')].some((f) => !f.querySelector('input:checked'));
        if (stickySelect) stickySelect.value = sizeOpen ? '' : variant.id;
        this.querySelectorAll('[data-sticky-variant-title]').forEach((el) => (el.textContent = variant.title));
        const thumb = this.querySelector('.cozy-sticky__thumb');
        const thumbSrc = variant.featured_media?.preview_image?.src;
        if (thumb && thumbSrc) {
          const url = new URL(thumbSrc, location.href);
          url.searchParams.set('width', '112');
          url.searchParams.set('height', '112');
          url.searchParams.set('crop', 'center');
          thumb.removeAttribute('srcset');
          thumb.src = url.href;
        }

        this.updateBreaks();
        this.updateTotal();
        this.updateOptionAvailability();

        this.showVariantMedia(variant);
        this.updateAvail(variant);
        // CRO 2026-10-20: the address no longer changes on a colour/size pick (it was rewritten with ?variant= on every
        // tap, and Clarity logged each one as a new page). The pick lives in the form; nothing reloads.
        this.announce(`${variant.title}, ${money(variant.price)}`);
      }

      // Follow-up 2026-10-20: the first garment size with nothing checked: show "בחרו מידה" in it, scroll to it.
      askSize() {
        const fs = [...this.querySelectorAll('[data-size-required]')].find((f) => !f.querySelector('input:checked'));
        if (!fs) return false;
        let msg = fs.querySelector('[data-size-need-msg]');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'cozy-variants__need';
          msg.setAttribute('role', 'alert');
          msg.dataset.sizeNeedMsg = '';
          fs.querySelector('legend').after(msg);
        }
        msg.textContent = `בחרו ${fs.dataset.sizeName || 'מידה'}`;
        msg.hidden = false;
        fs.classList.add('is-need');
        const calm = typeof cozyReduceMotion === 'function' && cozyReduceMotion();
        fs.scrollIntoView({ block: 'center', behavior: calm ? 'auto' : 'smooth' });
        fs.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
        this.announce?.(msg.textContent);
        return true;
      }

      hideSizeNeed() {
        this.querySelectorAll('[data-size-need-msg]').forEach((m) => (m.hidden = true));
        this.querySelectorAll('[data-size-required].is-need').forEach((f) => f.classList.remove('is-need'));
      }

      // CRO 2026-10-20: "which sizes in this colour" line under the picker ([data-variant-avail], Liquid renders the first).
      updateAvail(variant) {
        const el = this.querySelector('[data-variant-avail]');
        if (!el) return;
        const ci = Number(el.dataset.colourIndex);
        const si = Number(el.dataset.sizeIndex);
        const colour = variant.options[ci];
        if (colour === undefined) return;
        const same = this.product.variants.filter((v) => v.options[ci] === colour);
        el.textContent = '';
        const bdi = (t) => {
          const b = document.createElement('bdi');
          b.textContent = t;
          return b;
        };
        if (si >= 0) {
          const inStock = same.filter((v) => v.available).map((v) => v.options[si]);
          const out = same.filter((v) => !v.available).map((v) => v.options[si]);
          if (inStock.length) el.append(`מידות במלאי ב${colour}: `, bdi(inStock.join(' · ')));
          else el.append(`${colour}: אזל כרגע`);
          if (out.length) el.append(' · אזל: ', bdi(out.join(' · ')));
        } else {
          el.append(variant.available ? `${colour}: ${el.dataset.oneSize} · זמין להזמנה` : `${colour}: אזל כרגע`);
        }
      }

      // CRO 2026-10-20: the first photo of every colour, fetched once the shopper reaches for the colours (or when the
      // page is idle a few seconds after load), with the gallery's own srcset/sizes, so a colour change shows its
      // photo at once from the cache. Never before the load event, so the first photo (LCP) has the line to itself.
      warmColours() {
        if (this.warmed || !this.allSlides) return;
        this.warmed = true;
        const ids = new Set(this.product.variants.map((v) => v.featured_media && String(v.featured_media.id)).filter(Boolean));
        this.allSlides.forEach((slide) => {
          if (!ids.has(slide.dataset.mediaId)) return;
          const img = slide.querySelector('img');
          if (!img || img.complete && img.currentSrc) return;
          const srcset = img.dataset.srcset || img.getAttribute('srcset');
          const src = img.dataset.src || img.getAttribute('src');
          if (!src) return;
          const pre = new Image();
          pre.decoding = 'async';
          if (img.sizes) pre.sizes = img.sizes;
          if (srcset) pre.srcset = srcset;
          pre.src = src;
        });
      }

      // A value is "unavailable" when no available variant has it together with the other picks.
      updateOptionAvailability() {
        const selected = this.selectedOptions();
        this.querySelectorAll('[data-option-index]').forEach((input) => {
          const options = [...selected];
          options[Number(input.dataset.optionIndex)] = input.value;
          const ok = this.product.variants.some((v) => v.available && v.options.every((o, i) => options[i] === undefined || o === options[i]));
          input.closest('.cozy-pill')?.classList.toggle('is-unavailable', !ok);
        });
      }

      /* ---------- Quantity breaks + totals ---------- */

      tierTotal(el, price) {
        const qty = Number(el.dataset.qty);
        const fixed = Number(el.dataset.fixed) || 0;
        const full = price * qty;
        const total = fixed > 0 ? fixed : Math.round((full * (100 - Number(el.dataset.pct || 0))) / 100);
        return { qty, full, total };
      }

      updateBreaks() {
        const wrap = this.querySelector('[data-breaks]');
        if (!wrap) return;
        const money = (c) => cozyMoney(c, this.strings.money_format);
        wrap.querySelectorAll('[data-break]').forEach((el) => {
          const { qty, full, total } = this.tierTotal(el, this.variant.price);
          el.querySelector('[data-break-total]').textContent = money(total);
          const save = el.querySelector('[data-break-save]');
          save.hidden = full - total <= 0;
          if (!save.hidden) save.textContent = wrap.dataset.saveTemplate.replace('[amount]', money(full - total));
          const unit = el.querySelector('[data-break-unit]');
          if (unit) unit.textContent = this.strings.per_unit.replace('[price]', money(Math.round(total / qty)));
        });
      }

      updateTotal() {
        const checked = this.querySelector('[data-break-input]:checked')?.closest('[data-break]');
        const total = checked
          ? this.tierTotal(checked, this.variant.price).total
          : this.variant.price * (Number(this.qtyInput.value) || 1);
        const text = cozyMoney(total, this.strings.money_format);
        this.querySelectorAll('[data-total-price]').forEach((el) => (el.textContent = text));
        // The sticky bar says what one tap buys: the tier total, plus "× 2" when it is more than one.
        const qty = checked ? Number(checked.dataset.qty) : Number(this.qtyInput.value) || 1;
        this.querySelectorAll('[data-sticky-qty]').forEach((el) => {
          el.hidden = qty <= 1;
          el.textContent = '× ' + qty;
        });
      }

      /* ---------- Cart ---------- */

      async addItems(items, button) {
        // One add at a time: an impatient second tap must not add the tier twice.
        if (this.adding) return;
        this.adding = true;
        this.lastAtc = button || document.activeElement;
        const buttons = button ? [button] : [...this.querySelectorAll('[data-atc]')];
        buttons.forEach((b) => {
          b.classList.add('is-loading');
          b.setAttribute('aria-busy', 'true');
        });
        this.showError('');

        try {
          const drawer = document.querySelector('cart-drawer');
          const sections = drawer?.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [];
          const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || this.strings.error);

          this.form.classList.remove('is-added');
          void this.form.offsetWidth; // restart the steam animation
          this.form.classList.add('is-added');
          this.announce(this.strings.added);

          // No drawer took it (e.g. drawer disabled): fall back to the cart page.
          const handled = !document.dispatchEvent(new CustomEvent('cozy:cart-add', { detail: data, cancelable: true }));
          if (!handled) window.location.href = this.dataset.cartUrl;
        } catch (err) {
          this.showError(err.message || this.strings.error);
        } finally {
          this.adding = false;
          buttons.forEach((b) => {
            b.classList.remove('is-loading');
            b.removeAttribute('aria-busy');
          });
        }
      }

      showError(message) {
        const el = this.querySelector('[data-form-error]');
        if (!el) return;
        el.hidden = !message;
        el.textContent = message;
        // Tapped in the sticky bar? The message sits by the main button, so bring it into view.
        if (message && this.lastAtc?.closest?.('[data-sticky]')) {
          el.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      }

      announce(text) {
        if (!this.live) return;
        this.live.textContent = '';
        requestAnimationFrame(() => (this.live.textContent = text));
      }

      /* ---------- Kit details (every product page, the kit too) ---------- */

      // "פרטים ומידות" on the kit page (#kit-details) opens when a link targets it.
      initNotes() {
        this.openFromHash();
        window.addEventListener('hashchange', this.openFromHash);
        // A same-page link to it (the kit builder's "לטבלת המידות") opens it on every click, also when the hash is
        // already #kit-details (no hashchange then); the browser still scrolls to it.
        this.addEventListener('click', (e) => {
          const a = e.target.closest?.('a[href*="#"]');
          if (a && a.pathname === location.pathname && a.search === location.search) this.openFor(a.hash);
        });
      }

      openFromHash = () => this.openFor(location.hash);

      // Opens the <details> (in this product) that holds the hash's target.
      openFor(hash) {
        let id = hash.slice(1);
        try {
          id = decodeURIComponent(id);
        } catch (e) {} // a malformed hash is just not an id here
        const target = id && document.getElementById(id);
        const details = target && this.contains(target) ? target.closest('details') : null;
        if (details) details.open = true;
      }

      /* ---------- Gallery ---------- */

      initGallery() {
        const gallery = this.querySelector('[data-gallery]');
        this.track = gallery?.querySelector('[data-gallery-track]');
        if (!this.track) return;
        this.gallery = gallery;
        // All photos (every colour); this.slides/dots/thumbs = the visible ones (selected colour + shared).
        this.allSlides = [...gallery.querySelectorAll('.cozy-gallery__slide')];
        this.allDots = [...gallery.querySelectorAll('[data-gallery-dot]')];
        this.allThumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
        this.groupOption = Number(gallery.dataset.groupOption || 0);
        this.activeGroup = gallery.dataset.activeGroup;
        this.slides = this.allSlides.filter((s) => !s.hidden);
        this.dots = this.allDots.filter((d) => !d.hidden);
        this.thumbs = this.allThumbs.filter((t) => !t.hidden);
        this.index = 0;
        // Kit page that opens on a video: a muted loop, only when motion is welcome (its controls pause it).
        const autoplay = gallery.querySelector('[data-autoplay] video');
        if (autoplay && !cozyReduceMotion()) autoplay.play().catch(() => {});
        if (this.allSlides.length < 2) return; // one photo: nothing to move (goToMedia is a no-op)
        this.prevBtn = gallery.querySelector('[data-gallery-prev]');
        this.nextBtn = gallery.querySelector('[data-gallery-next]');
        this.rtl = getComputedStyle(this.track).direction === 'rtl';
        this.index = -1;
        this.setIndex(0);

        // Photos 2+ load after the first one is in, or as soon as the visitor touches the gallery.
        const first = this.slides[0].querySelector('img');
        const later = () => setTimeout(() => this.loadDeferred(), 300);
        if (first && !first.complete) first.addEventListener('load', later, { once: true });
        else later();
        ['pointerdown', 'focusin', 'scroll'].forEach((type) =>
          this.track.addEventListener(type, () => this.loadDeferred(), { once: true, passive: true })
        );

        let frame;
        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
              this.setIndex(Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth));
            });
          },
          { passive: true }
        );
        this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
        // Every thumb, not only the visible ones: another colour's thumbs show up after a colour change
        // (setGroup renumbers data-gallery-thumb), and they must work then too.
        this.allThumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.galleryThumb))));
        this.track.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          const forward = (e.key === 'ArrowLeft') === this.rtl;
          this.goTo(this.index + (forward ? 1 : -1));
        });
      }

      goTo(i, smooth = true) {
        if (!this.slides) return;
        this.loadDeferred();
        const index = Math.max(0, Math.min(this.slides.length - 1, i));
        this.track.scrollTo({
          left: (this.rtl ? -1 : 1) * index * this.track.clientWidth,
          behavior: smooth && !cozyReduceMotion() ? 'smooth' : 'auto',
        });
        this.setIndex(index);
      }

      loadDeferred() {
        this.slides?.forEach((slide) =>
          slide.querySelectorAll('img[data-src]').forEach((img) => {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
          })
        );
      }

      // Colour picked: show only that colour's photos (+ shared ones), its variant photo first.
      showVariantMedia(variant) {
        if (!this.allSlides || this.allSlides.length < 2) return;
        const group = variant.options[this.groupOption];
        const grouped = this.allSlides.some((s) => s.dataset.mediaGroup && s.dataset.mediaGroup !== '~shared');
        if (grouped && group !== this.activeGroup) this.setGroup(group, variant.featured_media?.id);
        else if (variant.featured_media) this.goToMedia(variant.featured_media.id);
      }

      setGroup(group, firstId) {
        this.activeGroup = group;
        const has = (g) => this.allSlides.some((s) => s.dataset.mediaGroup === g);
        const showAll = !has(group) && !has('~shared');
        const rank = (el) => {
          if (showAll || el.dataset.mediaGroup === group) return firstId && el.dataset.mediaId === String(firstId) ? 0 : 1;
          return el.dataset.mediaGroup === '~shared' ? 2 : 3;
        };
        // Dots and thumbs carry no media id: rank them through the slide at the same original position.
        const slideByPos = new Map(this.allSlides.map((s) => [s.dataset.mediaPos, s]));
        const rankOf = (el) => rank(slideByPos.get(el.dataset.mediaPos) || el);
        const arrange = (list) => {
          if (!list.length) return [];
          const parent = list[0].parentElement;
          const sorted = [...list].sort((a, b) => rankOf(a) - rankOf(b) || a.dataset.mediaPos - b.dataset.mediaPos);
          sorted.forEach((el) => {
            el.hidden = rankOf(el) === 3;
            parent.appendChild(el);
          });
          return sorted.filter((el) => !el.hidden);
        };
        this.slides = arrange(this.allSlides);
        this.dots = arrange(this.allDots);
        this.thumbs = arrange(this.allThumbs);
        this.thumbs.forEach((t, n) => {
          t.dataset.galleryThumb = n;
          t.setAttribute('aria-label', t.getAttribute('aria-label').replace(/\d+/, n + 1)); // "go to photo N"
        });
        this.slides.forEach((s, n) => s.setAttribute('aria-label', `${n + 1} / ${this.slides.length}`));
        const many = this.slides.length > 1;
        [this.prevBtn, this.nextBtn, this.gallery.querySelector('.cozy-gallery__dots'), this.gallery.querySelector('.cozy-gallery__thumbs')]
          .forEach((el) => el && (el.hidden = !many));
        this.loadDeferred();
        this.index = -1;
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        this.setIndex(0);
      }

      goToMedia(mediaId) {
        if (!this.slides || this.slides.length < 2) return;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i >= 0) this.goTo(i);
      }

      setIndex(i) {
        if (i === this.index) return;
        this.index = i;
        this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
        this.thumbs.forEach((t, n) => (n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
        // aria-disabled, not disabled: a disabled button would drop keyboard focus at the end.
        this.prevBtn?.setAttribute('aria-disabled', String(i === 0));
        this.nextBtn?.setAttribute('aria-disabled', String(i === this.slides.length - 1));
        // Pause a video that scrolled out.
        this.slides.forEach((s, n) => n !== i && s.querySelector('video')?.pause());
      }

      /* ---------- Sticky bar ---------- */

      // Visible whenever the main button is off screen (also before the visitor reaches it, so
      // price + buy are on the first phone screen), but never over the footer links.
      // Phones and tablets only (hidden from 990px in CSS). The bar shows once the main button has
      // scrolled up out of view (not while it is still below, e.g. right after load), and hides
      // again over the footer. It is position:fixed + transform, so it never shifts the layout.
      initSticky() {
        const sticky = this.querySelector('[data-sticky]');
        const main = this.querySelector('[data-main-atc]');
        if (!sticky || !main) return;
        const footer = document.querySelector('footer');
        // Phones/tablets in a viewport at least 450px tall (a landscape phone has no room for it).
        const small = matchMedia('(max-width: 989px) and (min-height: 450px)');
        let mainAbove = false;
        let footerVisible = false;
        const update = () => {
          const show = small.matches && mainAbove && !footerVisible;
          sticky.classList.toggle('is-visible', show);
          sticky.inert = !show;
        };
        // The root is stretched far below the viewport, so "not intersecting" can only mean "above",
        // even after an instant jump (anchor, back to top) that never crosses the viewport.
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => {
            mainAbove = !entry.isIntersecting;
            update();
          },
          // CRO 2026-10-20 (hoodie): the bar shows whenever the main button is off screen, also while it is still
          // below (a short in-app browser screen): no huge bottom margin then.
          { rootMargin: this.classList.contains('cozy-product--cro1') ? '0px' : '0px 0px 100000px 0px' }
        );
        this.stickyObserver.observe(main);
        if (footer) {
          this.footerObserver = new IntersectionObserver(([entry]) => {
            footerVisible = entry.isIntersecting;
            update();
          });
          this.footerObserver.observe(footer);
        }
        this.onStickyMedia = update;
        small.addEventListener('change', this.onStickyMedia);
        this.stickyMedia = small;
        // The floating accessibility button sits above the bar: publish the bar's real height.
        this.stickyResize = new ResizeObserver(() =>
          document.documentElement.style.setProperty('--cozy-sticky-h', `${Math.ceil(sticky.offsetHeight)}px`)
        );
        this.stickyResize.observe(sticky);
      }
    }
  );
}

// Size guide drawer (snippets/cozy-size-guide.liquid). showModal() makes the page inert (focus stays in
// the drawer) and Esc closes it natively; focus goes back to the button that opened it.
if (!customElements.get('cozy-size-guide')) {
  customElements.define(
    'cozy-size-guide',
    class CozySizeGuide extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-size-guide]');
        this.opener = this.querySelector('[data-size-guide-open]');
        if (!this.dialog || !this.opener) return;
        this.opener.addEventListener('click', () => {
          this.dialog.showModal();
          this.opener.setAttribute('aria-expanded', 'true');
        });
        this.dialog.addEventListener('close', () => {
          this.opener.setAttribute('aria-expanded', 'false');
          this.opener.focus();
        });
      }
    }
  );
}

cozyReveal();
document.addEventListener('shopify:section:load', (e) => cozyReveal(e.target));
